import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User, 
  Ship, 
  Globe, 
  Calendar, 
  Award, 
  FileCheck,
  Clock,
  MapPin,
  Briefcase,
  TrendingUp
} from 'lucide-react';
import type { CrewMember } from '../types';

interface CrewDetailModalProps {
  crew: CrewMember | null;
  isOpen: boolean;
  onClose: () => void;
}

export function CrewDetailModal({ crew, isOpen, onClose }: CrewDetailModalProps) {
  if (!crew) return null;

  const score = crew.match_score ?? 100;
  const formattedBirthDate = crew.birth_date 
    ? new Date(crew.birth_date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : 'N/A';
  const formattedSignOnDate = crew.sign_on_date 
    ? new Date(crew.sign_on_date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : 'N/A';
  const formattedDueDate = crew.due_soff_date 
    ? new Date(crew.due_soff_date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : 'N/A';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2
                       md:w-full md:max-w-2xl md:max-h-[90vh] overflow-auto
                       bg-white rounded-xl shadow-xl z-50"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Crew Member Details</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Profile Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{crew.name}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      ID: {crew.cid}
                    </span>
                    <span className="flex items-center gap-1">
                      <Award className="w-4 h-4" />
                      {crew.rank}
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">{score}%</div>
                  <div className="text-xs text-gray-500">Match Score</div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DetailItem 
                  icon={Globe} 
                  label="Nationality" 
                  value={crew.nationality} 
                />
                <DetailItem 
                  icon={Calendar} 
                  label="Birth Date" 
                  value={formattedBirthDate} 
                />
                <DetailItem 
                  icon={Ship} 
                  label="Current Vessel" 
                  value={crew.vessel || 'Not Assigned'} 
                />
                <DetailItem 
                  icon={MapPin} 
                  label="Next Vessel" 
                  value={crew.next_vessel || 'Not Assigned'} 
                />
                <DetailItem 
                  icon={Clock} 
                  label="Sign On Date" 
                  value={formattedSignOnDate} 
                />
                <DetailItem 
                  icon={Calendar} 
                  label="Due for Relief" 
                  value={formattedDueDate} 
                />
                <DetailItem 
                  icon={TrendingUp} 
                  label="Experience" 
                  value={`${crew.experience_months || 0} months in rank`} 
                />
                <DetailItem 
                  icon={FileCheck} 
                  label="Contracts Completed" 
                  value={String(crew.contracts || 0)} 
                />
                <DetailItem 
                  icon={Briefcase} 
                  label="Vessel Type Experience" 
                  value={crew.vessel_type || 'Not Specified'} 
                />
                <DetailItem 
                  icon={Award} 
                  label="Readiness Status" 
                  value={crew.readiness || 'Not Specified'} 
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                <button className="btn-primary flex-1">
                  Assign to Vessel
                </button>
                <button className="btn-secondary flex-1">
                  View Full Profile
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function DetailItem({ 
  icon: Icon, 
  label, 
  value 
}: { 
  icon: React.ComponentType<{ className?: string }>; 
  label: string; 
  value: string;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
      <div className="p-2 rounded-lg bg-white shadow-sm">
        <Icon className="w-4 h-4 text-primary-600" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}
